import { faStar, faHammer, faTrash, faFileInvoiceDollar, faCode, faFolderOpen, faDownload, faFilePdf, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from 'react-router-dom';
import { DeleteDesignForm, DesignIntakeForm, EditAddressForm, EngineerDesignForm, FavoriteDesignForm, ManageDesignTagsForm, OptimizeDesignForm, ShareDesignForm } from '../forms';
import { UnarchiveDesignForm } from '../forms/designs/UnarchiveDesignForm';
import { useAppContext } from '../../context/AppContext';
import { createIteratedDesign } from '../../utils/designIntake';
import { isEcoBeamDesign } from '../../utils/ecoBeamPricing';
import { findSkyCivPdfForDesign, openUploadInNewTab } from '../../utils/openUploadFile';
import { RenderDesigns } from '../blocks/Render';
import Modal from '../helpers/Modal';
import React, { useState, useCallback, useMemo } from 'react';
import { ActionsDropdown, ActionsDropdownItem, ActionsDropdownDivider } from './ActionsDropdown';
import { TableEmptyState } from './TableEmptyState';
import { isEmptyAddress } from '../../utils/projects';

const DesignFilterToggle = ({ label, checked, onChange }) => (
    <label className="archive-toggle">
        <span className="archive-toggle-label">{label}</span>
        <div
            className={`toggle-switch${checked ? ' toggle-switch--on' : ''}`}
            onClick={() => onChange(!checked)}
            role="switch"
            aria-checked={checked}
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onChange(!checked);
                }
            }}
        >
            <span className="toggle-switch-thumb" />
        </div>
    </label>
);

const formatDesignDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

const PRODUCT_TYPE_LABELS = {
    'standard-beam': 'Standard Beam',
    'eco-beam': 'Eco-Beam',
};

const formatProductType = (design) => {
    const productType = design.product_type ?? design.beam_series;
    if (!productType) return '—';
    return PRODUCT_TYPE_LABELS[productType] ?? productType;
};

const formatDesignJson = (raw) => {
    if (raw == null) return 'null';
    if (typeof raw === 'string') {
        try {
            return JSON.stringify(JSON.parse(raw), null, 2);
        } catch {
            return raw;
        }
    }
    try {
        return JSON.stringify(raw, null, 2);
    } catch {
        return String(raw);
    }
};

const sanitizeJsonFilename = (name) =>
    (name || 'design').replace(/[^a-zA-Z0-9-_]+/g, '-').replace(/^-+|-+$/g, '');

const downloadDesignJson = (design, key) => {
    const content = formatDesignJson(design[key]);
    const blob = new Blob([content], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${sanitizeJsonFilename(design.design_name)}-${key}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
};

const CompanyDesignsView = ({ designs }) => {
    const navigate = useNavigate();

    const handleOpenProject = (projectId) => {
        if (!projectId) return;
        navigate(`/project/${projectId}`);
    };

    return (
        <table className="data-table designs-table">
            <thead>
                <tr>
                    <th className="col-primary">Design</th>
                    <th className="col-lg">Project</th>
                    <th className="col-product-type hide-on-mobile">Product Type</th>
                    <th className="col-design-type hide-on-mobile">Type</th>
                    <th className="col-date hide-on-mobile">Created</th>
                    <th className="col-status hide-on-mobile">Status</th>
                    <th className="col-actions">Actions</th>
                </tr>
            </thead>
            <tbody>
                {designs.map((design) => (
                    <tr key={`${design.project_id}-${design.id}`}>
                        <td className="col-primary">
                            <div className="design-name-container">
                                <div className="design-name">{design.design_name || 'N/A'}</div>
                                <div className="design-sku-subline">{design.unique_id || '—'}</div>
                            </div>
                        </td>
                        <td className="col-lg">{design.project_name || `Project #${design.project_id}`}</td>
                        <td className="col-product-type hide-on-mobile">{formatProductType(design)}</td>
                        <td className="col-design-type hide-on-mobile">
                            {design.design_type ? design.design_type.toUpperCase() : 'NEW'}
                        </td>
                        <td className="col-date hide-on-mobile">{formatDesignDate(design.created_at)}</td>
                        <td className="col-status hide-on-mobile">
                            {design.is_archived ? 'Archived' : 'Active'}
                        </td>
                        <td className="col-actions">
                            <button
                                type="button"
                                onClick={() => handleOpenProject(design.project_id)}
                                className="btn btn-action btn-secondary"
                                title="Open Project"
                            >
                                <FontAwesomeIcon icon={faFolderOpen} /> Open
                            </button>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

const ProjectDesignsView = ({
    project,
    designs,
    setDesigns,
    archivedDesigns = [],
    setArchivedDesigns,
    files = [],
    showFailedToggle = false,
    buttonText = "Create Design for this Site Address",
    onGetQuoteForDesign,
}) => {
    const { user, token, base_url } = useAppContext();
    const [includeArchived, setIncludeArchived] = useState(false);
    const [includeFailed, setIncludeFailed] = useState(false);
    const [openDropdownId, setOpenDropdownId] = useState(null);
    const [jsonModal, setJsonModal] = useState(null);
    const [designIntakeConfig, setDesignIntakeConfig] = useState(null);
    const [shippingAddressPrompt, setShippingAddressPrompt] = useState(null);
    const [iteratingDesignId, setIteratingDesignId] = useState(null);

    const setDropdownOpen = useCallback((designId, open) => {
        setOpenDropdownId(open ? designId : null);
    }, []);

    const closeJsonModal = useCallback(() => setJsonModal(null), []);

    const failedActiveCount = useMemo(
        () => designs.filter((d) => d.design_passed === false).length,
        [designs]
    );

    const handleSetActiveDesigns = useCallback(
        (updater) => {
            setDesigns((prev) => {
                const next = typeof updater === 'function' ? updater(prev) : updater;
                if (!Array.isArray(next) || !setArchivedDesigns) return next;
                const removed = prev.filter((d) => !next.some((n) => n.id === d.id));
                if (removed.length > 0) {
                    setArchivedDesigns((archPrev) => [
                        ...removed.map((d) => ({ ...d, is_archived: true })),
                        ...archPrev.filter((a) => !removed.some((r) => r.id === a.id)),
                    ]);
                }
                return next;
            });
        },
        [setDesigns, setArchivedDesigns]
    );

    const handleSetArchivedDesigns = useCallback(
        (updater) => {
            if (!setArchivedDesigns) return;
            setArchivedDesigns((prev) => {
                const next = typeof updater === 'function' ? updater(prev) : updater;
                if (!Array.isArray(next)) return next;
                const removed = prev.filter((d) => !next.some((n) => n.id === d.id));
                if (removed.length > 0) {
                    setDesigns((activePrev) => [
                        ...removed.map((d) => ({ ...d, is_archived: false })),
                        ...activePrev.filter((a) => !removed.some((r) => r.id === a.id)),
                    ]);
                }
                return next;
            });
        },
        [setArchivedDesigns, setDesigns]
    );

    const updateDesigns = setArchivedDesigns ? handleSetActiveDesigns : setDesigns;

    const visibleDesigns = useMemo(() => {
        const active = designs.filter((d) => includeFailed || d.design_passed === true);
        const archived = includeArchived ? archivedDesigns : [];
        return [...active, ...archived];
    }, [designs, archivedDesigns, includeFailed, includeArchived]);

    const passedActiveCount = useMemo(
        () => designs.filter((d) => d.design_passed === true).length,
        [designs]
    );
    
    const openCreateDesignIntake = () => {
        const projectName = project?.project_name || project?.name || project?.title || '';
        setDesignIntakeConfig({
            design: null,
            initialDesignName: designs.length === 0 ? projectName : '',
        });
    };

    const iterateDesign = async (design) => {
        setOpenDropdownId(null);
        if (iteratingDesignId) return;
        setIteratingDesignId(design.id);
        try {
            const existingNames = [...designs, ...archivedDesigns]
                .map((d) => d?.design_name)
                .filter(Boolean);
            await createIteratedDesign({
                token,
                base_url,
                project,
                sourceDesign: design,
                existingNames,
                userEmail: user?.email,
                setDesigns,
            });
        } catch (error) {
            console.error('Error iterating design:', error);
            if (window.showAlert) {
                window.showAlert(500, error?.message || 'Failed to create iterated design. Please try again later.');
            }
        } finally {
            setIteratingDesignId(null);
        }
    };

    const restrictedStages = ['prepare to build', 'manufacturing', 'shipped'];
    const canCreateDesign = !restrictedStages.includes(project?.current_stage) || user?.is_super_admin === true;
    const isQuoteStageRestricted =
        project?.current_stage === 'manufacturing' || project?.current_stage === 'shipped';
    const showGetQuoteAction =
        typeof onGetQuoteForDesign === 'function' &&
        project?.company_id !== 1 &&
        !isQuoteStageRestricted;

    const handleGetQuoteForDesign = (design) => {
        if (!showGetQuoteAction || isEcoBeamDesign(design)) return;
        if (isEmptyAddress(project?.shipping_address)) {
            setShippingAddressPrompt(Date.now());
            return;
        }
        onGetQuoteForDesign(design);
    };

    const handleViewSkycivPdf = async (design) => {
        if (!design?.id) {
            window.showAlert(
                404,
                'No documents were found for this design. Please review the Files tab.'
            );
            return;
        }
        const upload = findSkyCivPdfForDesign(files, design.id);
        if (!upload) {
            window.showAlert(
                404,
                'No documents were found for this design. Please review the Files tab.'
            );
            return;
        }
        setOpenDropdownId(null);
        await openUploadInNewTab(upload, token, base_url);
    };

    const showDesignFilters =
        archivedDesigns.length > 0 || (showFailedToggle && failedActiveCount > 0);

    return (
        <div>
            <div className="designs-header">
                <div className="designs-header-main">
                    {canCreateDesign && (
                        <button
                            onClick={openCreateDesignIntake}
                            className="btn btn-action btn-primary"
                            title="Create Design"
                        >
                            <FontAwesomeIcon icon={faHammer} /> {buttonText}
                        </button>
                    )}
                    {!canCreateDesign && (
                        <div className="banner banner--info banner--sm">
                            Creating new designs is not available for this project stage. If you need to change your order now that it&apos;s in process, please contact support.
                        </div>
                    )}
                    <div className="designs-tips">
                        <span className="designs-tip">
                            <FontAwesomeIcon icon={faStar} className="icon-star" /> Favorite your most recent designs
                        </span>
                        <span className="designs-tip">
                            <span className="tip-color-indicator quoted"></span> Quoted designs are highlighted
                        </span>
                        <span className="designs-tip">
                            <FontAwesomeIcon icon={faTrash} className="icon-muted" /> Archive old designs you no longer need
                        </span>
                    </div>
                </div>
                {showDesignFilters && (
                    <div className="designs-filters">
                        {archivedDesigns.length > 0 && (
                            <DesignFilterToggle
                                label="Include archived"
                                checked={includeArchived}
                                onChange={setIncludeArchived}
                            />
                        )}
                        {showFailedToggle && failedActiveCount > 0 && (
                            <DesignFilterToggle
                                label="Include failed"
                                checked={includeFailed}
                                onChange={setIncludeFailed}
                            />
                        )}
                    </div>
                )}
            </div>
            {passedActiveCount > 5 && (
                <div className="banner banner--warning banner--row">
                    <FontAwesomeIcon icon={faTrash} className="icon-muted-gap" />
                    You have {passedActiveCount} active designs. Consider archiving older designs you no longer need to keep the list manageable (You can always restore them later).
                </div>
            )}
            <br />
            {visibleDesigns.length === 0 ? (
                <TableEmptyState>
                    <p>
                        {includeArchived || includeFailed
                            ? 'No designs match the current filters.'
                            : 'No designs available for this project.'}
                    </p>
                    {!includeArchived && !includeFailed && (
                        canCreateDesign ? (
                            <p>Click the "Create Design" button above to create your first design.</p>
                        ) : (
                            <p>Creating new designs is not available for this project stage.</p>
                        )
                    )}
                </TableEmptyState>
            ) : (
                <div className="table-wrapper">
                <table className="data-table designs-table">
                    <thead>
                        <tr>
                            <th className="col-primary">Name</th>
                            <th className="col-product-type hide-on-mobile hide-below-xl">Product Type</th>
                            <th className="col-design-type hide-on-mobile">Type</th>
                            <th className="col-date hide-on-mobile">Created</th>
                            <th className="col-favorite hide-on-mobile" title="Favorite">
                                <FontAwesomeIcon icon={faStar} className="icon-star" aria-hidden />
                                <span className="sr-only">Favorite</span>
                            </th>
                            <th className="col-actions--wide design-actions-heading">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visibleDesigns
                            .sort((a, b) => {
                                const displayTier = (d) => {
                                    if (d.is_favorited) return 0;
                                    if (d.is_quoted) return 1;
                                    return 2;
                                };
                                const tierA = displayTier(a);
                                const tierB = displayTier(b);
                                if (tierA !== tierB) return tierA - tierB;
                                const dateA = new Date(a.created_at || 0).getTime();
                                const dateB = new Date(b.created_at || 0).getTime();
                                return dateB - dateA;
                            })
                            .map((design, index) => {
                            const isArchived = Boolean(design.is_archived);
                            const isFailed = design.design_passed === false;
                            const isAwaitingSku = design.unique_id === 'awaiting-sku';
                            const rowClass = [
                                design.is_quoted ? 'table-row--highlighted' : '',
                                isArchived ? 'design-is-archived' : '',
                                isFailed ? 'design-is-failed' : '',
                            ]
                                .filter(Boolean)
                                .join(' ');
                            return (
                            <tr key={design.id || index} className={rowClass}>
                                <td className="col-primary">
                                    <div className="design-name-container">
                                        <div className="design-name">{design.design_name || 'N/A'}</div>
                                        <div className="design-sku-subline">{design.unique_id}</div>
                                        {(design.op_package_ordered || design.pe_stamps_ordered) && (
                                            <div className="design-sku-badges">
                                                {design.op_package_ordered && (
                                                    <span className="status-chip status-chip--xs status-chip--ordered">Optimization Ordered</span>
                                                )}
                                                {design.pe_stamps_ordered && (
                                                    <span className="status-chip status-chip--xs status-chip--ordered">PE Stamp Ordered</span>
                                                )}
                                            </div>
                                        )}
                                        {isArchived && (
                                            <span className="status-chip status-chip--xs status-chip--archived">Archived</span>
                                        )}
                                        {!isArchived && isFailed && (
                                            <span className="status-chip status-chip--xs status-chip--danger">Failed</span>
                                        )}
                                    </div>
                                </td>
                                <td className="col-product-type hide-on-mobile hide-below-xl">{formatProductType(design)}</td>
                                <td className="col-design-type hide-on-mobile">{design.design_type ? design.design_type.toUpperCase() : 'NEW'}</td>
                                <td className="col-date hide-on-mobile">
                                    {design.created_at ? new Date(design.created_at).toLocaleDateString('en-US', { 
                                        year: 'numeric', 
                                        month: 'short', 
                                        day: 'numeric' 
                                    }) : 'N/A'}
                                </td>
                                <td className="col-favorite hide-on-mobile">
                                    {design.is_favorited ? (
                                        <FontAwesomeIcon 
                                            icon={faStar} 
                                            className="icon-star"
                                            title="Favorited"
                                        />
                                    ) : (
                                        <span className="text-em-dash">—</span>
                                    )}
                                </td>
                                <td className={`col-actions--wide design-actions-cell${openDropdownId === design.id ? ' design-actions-cell--open' : ''}`}>
                                    <div className="design-actions-row">
                                        {!isArchived && !isAwaitingSku && !isFailed && showGetQuoteAction && !isEcoBeamDesign(design) && (
                                            <button
                                                type="button"
                                                className="btn btn-action btn-secondary get-quote-design-btn hide-on-mobile"
                                                onClick={() => handleGetQuoteForDesign(design)}
                                                title="Add to Quote"
                                            >
                                                <FontAwesomeIcon icon={faFileInvoiceDollar} />
                                                <span>Add to Quote</span>
                                            </button>
                                        )}
                                    {isArchived ? (
                                        <ActionsDropdown
                                            isOpen={openDropdownId === design.id}
                                            onOpenChange={(open) => setDropdownOpen(design.id, open)}
                                            estimatedMenuHeight={400}
                                        >
                                            <ActionsDropdownItem>
                                                <RenderDesigns design={design} />
                                            </ActionsDropdownItem>
                                            <ActionsDropdownItem>
                                                <button
                                                    type="button"
                                                    className="btn btn-action btn-secondary"
                                                    onClick={() => handleViewSkycivPdf(design)}
                                                    title="View SkyCiv PDF"
                                                >
                                                    <FontAwesomeIcon icon={faFilePdf} />
                                                    <span>View Skyciv PDF</span>
                                                </button>
                                            </ActionsDropdownItem>
                                            <ActionsDropdownItem>
                                                <UnarchiveDesignForm
                                                    design={design}
                                                    setDesigns={handleSetArchivedDesigns}
                                                />
                                            </ActionsDropdownItem>
                                        </ActionsDropdown>
                                    ) : isAwaitingSku ? (
                                        <ActionsDropdown
                                            isOpen={openDropdownId === design.id}
                                            onOpenChange={(open) => setDropdownOpen(design.id, open)}
                                            estimatedMenuHeight={400}
                                        >
                                            <ActionsDropdownItem>
                                                <button
                                                    className="btn btn-action btn-secondary"
                                                    onClick={() => iterateDesign(design)}
                                                    disabled={iteratingDesignId === design.id}
                                                >
                                                    <FontAwesomeIcon icon={iteratingDesignId === design.id ? faSpinner : faHammer} spin={iteratingDesignId === design.id} />
                                                    <span>{iteratingDesignId === design.id ? 'Iterating...' : 'Iterate Design'}</span>
                                                </button>
                                            </ActionsDropdownItem>
                                            <ActionsDropdownItem>
                                                <button
                                                    type="button"
                                                    className="btn btn-action btn-secondary"
                                                    onClick={() => {
                                                        setJsonModal({ design, key: 'intake' });
                                                        setOpenDropdownId(null);
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faCode} />
                                                    <span>View JSON</span>
                                                </button>
                                            </ActionsDropdownItem>
                                            <ActionsDropdownDivider />
                                            <ActionsDropdownItem>
                                                <DeleteDesignForm design={design} setDesigns={updateDesigns} />
                                            </ActionsDropdownItem>
                                        </ActionsDropdown>
                                    ) : isFailed ? (
                                        <button
                                            type="button"
                                            className="btn btn-action btn-secondary"
                                            onClick={() => setJsonModal({ design, key: 'sky_civ_details' })}
                                            title="View sky_civ_details JSON"
                                        >
                                            <FontAwesomeIcon icon={faCode} />
                                            <span>View JSON</span>
                                        </button>
                                    ) : (
                                        <ActionsDropdown
                                            isOpen={openDropdownId === design.id}
                                            onOpenChange={(open) => setDropdownOpen(design.id, open)}
                                            estimatedMenuHeight={400}
                                        >
                                            {showGetQuoteAction && !isEcoBeamDesign(design) && (
                                                <>
                                                    <ActionsDropdownItem className="show-on-mobile-only">
                                                        <button
                                                            type="button"
                                                            className="btn btn-action btn-primary"
                                                            onClick={() => {
                                                                handleGetQuoteForDesign(design);
                                                                setOpenDropdownId(null);
                                                            }}
                                                            title="Add to Quote"
                                                        >
                                                            <FontAwesomeIcon icon={faFileInvoiceDollar} />
                                                            <span>Add to Quote</span>
                                                        </button>
                                                    </ActionsDropdownItem>
                                                    <ActionsDropdownDivider className="show-on-mobile-only" />
                                                </>
                                            )}
                                            <ActionsDropdownItem>
                                                <RenderDesigns design={design} />
                                            </ActionsDropdownItem>
                                            <ActionsDropdownItem>
                                                <button
                                                    type="button"
                                                    className="btn btn-action btn-secondary"
                                                    onClick={() => handleViewSkycivPdf(design)}
                                                    title="View SkyCiv PDF"
                                                >
                                                    <FontAwesomeIcon icon={faFilePdf} />
                                                    <span>View Skyciv PDF</span>
                                                </button>
                                            </ActionsDropdownItem>
                                            <ActionsDropdownItem>
                                                <button
                                                    type="button"
                                                    className="btn btn-action btn-secondary"
                                                    onClick={() => iterateDesign(design)}
                                                    disabled={iteratingDesignId === design.id}
                                                >
                                                    <FontAwesomeIcon icon={iteratingDesignId === design.id ? faSpinner : faHammer} spin={iteratingDesignId === design.id} />
                                                    <span>{iteratingDesignId === design.id ? 'Iterating...' : 'Iterate Design'}</span>
                                                </button>
                                            </ActionsDropdownItem>
                                            {(!design.pe_stamps_ordered || !design.op_package_ordered) && (
                                                <ActionsDropdownDivider />
                                            )}
                                            {!design.pe_stamps_ordered && (
                                                <ActionsDropdownItem>
                                                    <EngineerDesignForm project_id={project.id} design_id={design.id} />
                                                </ActionsDropdownItem>
                                            )}
                                            {!design.op_package_ordered && (
                                                <ActionsDropdownItem>
                                                    <OptimizeDesignForm project_id={project.id} design_id={design.id} />
                                                </ActionsDropdownItem>
                                            )}
                                            <ActionsDropdownDivider />
                                            {user.is_super_admin === true && (
                                                <>
                                                    <ActionsDropdownItem>
                                                        <ManageDesignTagsForm design={design} setDesigns={updateDesigns} />
                                                    </ActionsDropdownItem>
                                                    <ActionsDropdownItem>
                                                        <ShareDesignForm project_id={project.id} design_id={design.id} />
                                                    </ActionsDropdownItem>
                                                </>
                                            )}
                                            <ActionsDropdownItem>
                                                <FavoriteDesignForm design={design} setDesigns={updateDesigns} />
                                            </ActionsDropdownItem>
                                            <ActionsDropdownItem>
                                                <DeleteDesignForm design={design} setDesigns={updateDesigns} />
                                            </ActionsDropdownItem>
                                        </ActionsDropdown>
                                    )}
                                    </div>
                                </td>
                            </tr>
                            );
                        })}
                    </tbody>
                </table>
                </div>
            )}
            {shippingAddressPrompt && (
                <EditAddressForm
                    key={shippingAddressPrompt}
                    project={project}
                    addressType="shipping"
                    autoOpen
                    hideButton
                    notice="A shipping address is required to get a quote. Please enter one below, then try Add to Quote again."
                    onClose={() => setShippingAddressPrompt(null)}
                />
            )}
            {designIntakeConfig && (
                <DesignIntakeForm
                    project={project}
                    design={designIntakeConfig.design}
                    setDesigns={setDesigns}
                    initialDesignName={designIntakeConfig.initialDesignName}
                    onClose={() => setDesignIntakeConfig(null)}
                />
            )}
            {jsonModal && (
                <Modal
                    isWide
                    setIsModalOpen={closeJsonModal}
                    form={
                        <div className="designs-table-modal-body">
                            <div className="designs-table-modal-header">
                                <h3 className="designs-table-modal-title">
                                    {jsonModal.design.design_name || 'Design'} — {jsonModal.key}
                                </h3>
                                <button
                                    type="button"
                                    className="btn btn-action btn-secondary"
                                    onClick={() => downloadDesignJson(jsonModal.design, jsonModal.key)}
                                >
                                    <FontAwesomeIcon icon={faDownload} /> Download JSON
                                </button>
                            </div>
                            <pre className="designs-table-modal-code">
                                {formatDesignJson(jsonModal.design[jsonModal.key])}
                            </pre>
                        </div>
                    }
                />
            )}

        </div>
    );
};

export const DesignsTable = ({
    variant = 'project',
    designs = [],
    ...projectProps
}) => {
    if (variant === 'company') {
        return <CompanyDesignsView designs={designs} />;
    }
    return <ProjectDesignsView designs={designs} {...projectProps} />;
};